/**
 * Pure join of a profile's per-game statistics with the game catalog and the
 * profile artwork map, producing the data a single reusable
 * `GameProfileStatsCard` renders for *any* game (APP_SPEC §13.3). No
 * per-`gameId` branching — a game contributes a card purely by having a
 * `gameStatistics` row and (optionally) registered art.
 */
import type { GameCatalogEntry, GameStatistics } from '@perplexicon/api-client';

import type { GameProfileArt } from '../../assets/asset-manifest.js';
import { resolveGameProfileArt } from '../../assets/asset-manifest.js';
import { GAME_CATALOG_ART, GAME_PROFILE_ART } from '../../games/registry.js';
import { formatStatValue } from './format-stat.js';
import { gameStatIcon } from './profile-assets.js';

export interface GameStatCell {
  key: string;
  iconSrc: string;
  label: string;
  value: string;
}

export interface GameCardData {
  id: string;
  name: string;
  art: GameProfileArt;
  statCells: GameStatCell[];
  streak: { currentDays: number; bestDays: number };
}

export function buildGameCardData(
  gameStatistics: readonly GameStatistics[],
  catalog: readonly GameCatalogEntry[],
): GameCardData[] {
  const byId = new Map(catalog.map((game) => [game.gameId, game]));

  return gameStatistics.map((entry) => {
    const meta = byId.get(entry.gameId);
    const allDescriptors = meta?.statisticDescriptors ?? [];
    // A game with more tracked metrics than fit a compact card can feature
    // a curated subset via `profileStatKeys` (e.g. Word Delve: 15 tracked,
    // 6 featured) without deleting the rest — absent, every descriptor is
    // shown, unchanged from before this field existed.
    const descriptors = meta?.profileStatKeys
      ? meta.profileStatKeys
          .map((key) => allDescriptors.find((d) => d.key === key))
          .filter((d): d is (typeof allDescriptors)[number] => d !== undefined)
      : allDescriptors;

    return {
      id: entry.gameId,
      name: meta?.name ?? entry.gameId,
      art: resolveGameProfileArt(GAME_CATALOG_ART[entry.gameId], GAME_PROFILE_ART[entry.gameId]),
      statCells: descriptors.map((descriptor) => ({
        key: descriptor.key,
        iconSrc: gameStatIcon(descriptor.key),
        label: descriptor.label,
        value: formatStatValue(entry.metrics[descriptor.key] ?? 0, descriptor.format),
      })),
      streak: { currentDays: entry.currentStreakDays, bestDays: entry.bestStreakDays },
    };
  });
}
